import React, { useState, useLayoutEffect, useEffect } from "react";

/** Components */
import { TableContainer, Table, TableHead, TableBody, TableRow, TableCell, TableFooter, Paper, TablePagination, ButtonGroup, Button, Collapse, Tooltip } from "@mui/material";
import LoadingProgress from "../../commons/LoadingProgress.component";
import { FilterBy } from "../../commons/filter.component";
import ButtonWithAPICall from "../../commons/ButtonWithAPICall.component";
import CustomDatePicker from "../../commons/CustomDatePicker";

/** Icons */
import SearchIcon from "@mui/icons-material/Search";

/** Api & Function */
import { getFuelLogbox, getOrganizationData, getUsers, getVehicle } from "../../../services/api";
import { fitToColumn } from "../../../services/libService";
import { setHeaderTitle } from "../../../store/layoutSlide";

/** Package */
import moment from "moment";
import * as XLSX from "xlsx";

/** Hook */
import { ExcelIcon } from "../../commons/miscellaneous";
import { useTranslation } from "react-i18next";
import { useParamFilter } from "../../commons/customHook";
import { useSelector, useDispatch } from "react-redux";

const FuelLogbox = () => {
  const [isSearchOpen, setSearchOpen] = useState(true);
  const dispatch = useDispatch();

  const [t] = useTranslation();
  const [input, setInput] = useState({
    staff: null,
    start_date: "",
    end_date: "",
    vehicle: "",
    organization: null,
  });
  const { fetchData, data, page, perPage, setPage, setPerPage, total, filter, isAllow, loading } = useParamFilter({ page: 1, per_page: 50 }, getFuelLogbox);
  const user = useSelector((state) => state.user.data);
  const handleClickDownloadCSV = () => {
    const params = {};
    if (input.staff) params.staff_id = input.staff.id;
    if (input.start_date) params.start_date = input.start_date;
    if (input.end_date) params.end_date = input.end_date;
    if (input.vehicle) params.vehicle_id = input.vehicle.id;
    if (input.organization) params.organization_id = input.organization.id;

    return getFuelLogbox(params);
  };
  const handleSearch = () => {
    fetchData((prev) => ({ ...input, staff_id: input.staff?.id, staff: null, vehicle_id: input.vehicle?.id, vehicle: null, organization: null, organization_id: input.organization?.id }));
  };
  const exportExcel = (res) => {
    if (res.code === 200) {
      const arrayData = res.data.map((item) => ({
        // làm thế này để xác định những cột nào định dạng string trong file csv
        ID: item.id,
        "Thời điểm": item.date ? moment(item.date).format("HH:mm DD/MM/YYYY") : "",
        "Biển số xe": item.vehicle,
        "Chuyến bay": item.flight_no,
        "Loại tàu bay": item.aircraft_type,
        "Số công tơ đầu": +item.start_pump,
        "Lượng nạp/bơm quẩn (USG)": +item.loadsample,
        "Lưu lượng (max)(Gpm)": +item.maxflow,
        "Chênh áp": +item.diffpres,
        "Áp suất đầu vòi (max)(psi)": +item.nozzlepres,
        "Thử nước": +item.chk_water,
        "Hãng HK": item.customer_name,
        "Người vận hành": item.staff_name,
        "Trạng thái": t(item.status),
      }));
      const worksheet = XLSX.utils.json_to_sheet(arrayData);
      worksheet["!cols"] = fitToColumn(arrayData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet);
      XLSX.writeFileXLSX(workbook, "stdtn.xlsx");
    }
  };

  useLayoutEffect(() => {
    fetchData(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(setHeaderTitle("SỔ THEO DÕI TRA NẠP"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Main Render
  return (
    <div>
      <div align="right" style={{ marginBottom: 10, marginTop: 10, backgroundColor: "#b3deff", height: 40 }}>
        <ButtonGroup style={{ height: 40, marginRight: 10 }}>
          <ButtonWithAPICall disableSnackbar={() => true} response={exportExcel} api={handleClickDownloadCSV} style={{ display: Boolean(data.length) ? "" : "none" }}>
            <Tooltip title="Xuất dữ liệu file Excel">
              <ExcelIcon />
            </Tooltip>
          </ButtonWithAPICall>
          <Button onClick={() => setSearchOpen((prev) => !prev)} variant="outlined">
            <Tooltip title="Thư mục">
              <SearchIcon />
            </Tooltip>
          </Button>
        </ButtonGroup>
      </div>
      <Collapse in={isSearchOpen}>
        <FilterBy
          style={{ display: "inline-flex", margin: "5px", minWidth: 240 }}
          getDataFunc={() => getOrganizationData({ status: 1 })}
          value={input.organization || null}
          onChange={(e, newValue) => setInput((prev) => ({ ...prev, organization: newValue }))}
          label={t("Chi nhánh")}
        />
        <FilterBy
          style={{ display: "inline-flex", margin: "5px", minWidth: 240 }}
          getDataFunc={() => getUsers({ organization_id: user.organization_id })}
          value={input.staff || null}
          onChange={(e, newValue) => setInput((prev) => ({ ...prev, staff: newValue }))}
          label={t("Người vận hành")}
          setDefaultFunc={(option) => setInput((prev) => ({ ...prev, staff: option.find((item) => item.id == filter.staff_id) }))}
          triggerSetDefaultFunc={true}
          setDefaultDependencies={[filter.staff_id]}
        />
        <FilterBy
          style={{ display: "inline-flex", margin: "5px", minWidth: 240 }}
          getDataFunc={() => getVehicle({ organization_id: user.organization_id })}
          value={input.vehicle || null}
          onChange={(e, newValue) => setInput((prev) => ({ ...prev, vehicle: newValue }))}
          label={t("Biển số xe")}
          getOptionLabel={(option) => `${option.license_plate}` || ""}
          setDefaultFunc={(option) => setInput((prev) => ({ ...prev, vehicle: option.find((item) => item.id == filter.vehicle_id) }))}
          triggerSetDefaultFunc={true}
          setDefaultDependencies={[filter.vehicle_id]}
        />
        <CustomDatePicker
          format="DD/MM/YYYY"
          label={t("Ngày bắt đầu")}
          value={input.start_date}
          onChange={(value) => setInput((prev) => ({ ...prev, start_date: value }))}
          sx={{ minWidth: "200px", margin: "5px" }}
        />
        <CustomDatePicker
          format="DD/MM/YYYY"
          label={t("Ngày kết thúc")}
          value={input.end_date}
          onChange={(value) => setInput((prev) => ({ ...prev, end_date: value }))}
          sx={{ minWidth: "200px", margin: "5px" }}
        />
        <Button variant="outlined" onClick={handleSearch} style={{ margin: "10px 0" }}>
          {t("Tìm kiếm")}
        </Button>
      </Collapse>
      {loading ? (
        <LoadingProgress />
      ) : (
        <TableContainer component={Paper} style={{ width: "98%", marginLeft: 10 }} sx={{ maxHeight: 800 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                  ID
                </TableCell>
                <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                  {t("Xe tra nạp")}
                </TableCell>
                <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                  {t("Thời điểm")}
                </TableCell>
                <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                  {t("Số hiệu chuyến bay")}
                </TableCell>
                <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                  {t("Số công tơ đầu")}
                </TableCell>
                <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                  {t("Lượng nạp (USG)")}
                </TableCell>

                <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                  {t("Loại tàu bay")}
                </TableCell>
                <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                  {t("Lưu lượng (max)(Gpm)")}
                </TableCell>
                <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                  {t("Chênh áp")}
                </TableCell>
                <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                  {t("Áp suất đầu vòi (max)(psi)")}
                </TableCell>
                <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                  {t("Thử nước")}
                </TableCell>
                <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                  {t("Hãng HK")}
                </TableCell>
                <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                  {t("Người vận hành")}
                </TableCell>
                <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                  {t("Trạng thái")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.length ? (
                data.map((row, index) => {
                  return (
                    <TableRow key={row.id}>
                      <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                        {row.id}{" "}
                      </TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                        {row.vehicle}
                      </TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                        {moment(row.date).format("HH:mm DD/MM/YYYY")}
                      </TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                        {row.flight_no}
                      </TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                        {row.start_pump?.toLocaleString()}
                      </TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                        {row.loadsample?.toLocaleString()}
                      </TableCell>

                      <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                        {row.aircraft_type}
                      </TableCell>

                      <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                        {row.maxflow?.toLocaleString()}
                      </TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                        {row.diffpres?.toLocaleString()}
                      </TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                        {row.nozzlepres?.toLocaleString()}
                      </TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                        {row.chk_water}
                      </TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }} align="left">
                        {row.customer_name}
                      </TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }} align="left">
                        {row.staff_name}
                      </TableCell>
                      <TableCell style={{ border: "0.5px solid lightgray" }} align="center">
                        {t(row.status)}
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={20}>
                    {t(isAllow ? "Không có dữ liệu" : "No permission")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination rowsPerPageOptions={[50, 100, 150]} count={total || 0} rowsPerPage={perPage} page={page} onPageChange={setPage} onRowsPerPageChange={setPerPage} />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};

export default FuelLogbox;
